/**
 * usePushNotifications — Landlord
 * Handles FCM token acquisition, device registration, and foreground message handling.
 *
 * - requestPermissionAndRegister: requests Notification permission, gets an FCM token,
 *   registers the device via the API, and persists the device ID in localStorage.
 * - deregister: deletes the device registration from the API (best-effort, does not throw)
 *   and clears localStorage. Call BEFORE logging out.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../lib/firebase';
import { registerDevice, deleteDevice, ApiClientError } from '../lib/api';
import { reportError } from '../lib/error-reporter';

const DEVICE_ID_KEY = 'pwa_device_registration_id';
const SYNCED_TOKEN_KEY = 'pwa_fcm_token_synced';
const SYNCED_AT_KEY = 'pwa_fcm_token_synced_at';
const PENDING_SYNC_KEY = 'pwa_push_pending_sync';
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

// Firebase's own guidance: no onTokenRefresh in the modern JS SDK, so a
// rotated token is only discovered by re-calling getToken() periodically.
// A week bounds how long a silently-rotated token can go unreconciled.
const RECONCILE_STALE_MS = 7 * 24 * 60 * 60 * 1000;

export interface UsePushNotificationsOptions {
  /**
   * Show a native Notification when a push arrives while this tab is
   * foregrounded. FCM never invokes the service worker's
   * onBackgroundMessage while the app is foregrounded — it delivers to
   * onMessage() here instead — so without this, a foreground push is
   * silently dropped no matter how reliably it was sent server-side.
   * Exactly one mounted instance should own this (AuthProvider, which lives
   * for the whole app session); other call sites must pass false or the
   * same push shows twice.
   */
  handleForegroundMessages?: boolean;
}

export interface UsePushNotificationsReturn {
  permissionState: NotificationPermission | 'unsupported';
  isRegistered: boolean;
  requestPermissionAndRegister: () => Promise<void>;
  deregister: () => Promise<void>;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {}
): UsePushNotificationsReturn {
  const { handleForegroundMessages = true } = options;
  const navigate = useNavigate();
  const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>(() => {
    if (!('Notification' in window) || !messaging) return 'unsupported';
    return Notification.permission;
  });
  const [isRegistered, setIsRegistered] = useState(() => {
    return !!localStorage.getItem(DEVICE_ID_KEY);
  });

  const requestPermissionAndRegister = useCallback(async () => {
    if (!messaging) throw new Error('Push notifications are not supported in this browser');

    const permission = await Notification.requestPermission();
    setPermissionState(permission);
    if (permission !== 'granted') return;

    let swRegistration: ServiceWorkerRegistration;
    try {
      swRegistration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL}firebase-messaging-sw.js`,
        { updateViaCache: 'none' }
      );
    } catch (err) {
      reportError(err, { step: 'sw-register' });
      throw new Error('Failed to enable push notifications');
    }

    let token: string;
    try {
      token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });
    } catch (err) {
      reportError(err, { step: 'fcm-get-token' });
      throw new Error('Failed to enable push notifications');
    }

    try {
      const { device } = await registerDevice({
        platform: 'web',
        fcmToken: token,
        appType: 'landlord',
        deviceName: navigator.userAgent.slice(0, 100),
        previousDeviceId: localStorage.getItem(DEVICE_ID_KEY) ?? undefined,
      });
      localStorage.setItem(DEVICE_ID_KEY, device.id);
      localStorage.setItem(SYNCED_TOKEN_KEY, token);
      localStorage.setItem(SYNCED_AT_KEY, String(Date.now()));
      localStorage.removeItem(PENDING_SYNC_KEY);
      setIsRegistered(true);
    } catch (err) {
      reportError(err, { step: 'register-device' });
      throw new Error('Failed to enable push notifications');
    }
  }, []);

  const deregister = useCallback(async () => {
    const deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) return;
    try {
      await deleteDevice(deviceId);
    } catch (err) {
      // A 404 means the row is already gone server-side — equivalent to
      // success, clear local state below. Any other failure (network,
      // 500, session expiry mid-toggle) means the server-side row is
      // still active: keep DEVICE_ID_KEY so the next registration sends
      // it as previousDeviceId and reuses/retires it, instead of
      // orphaning it as a permanently-active row that still receives
      // pushes alongside whatever gets registered next (don't block the
      // toggle on this — just don't lose the pointer).
      if (!(err instanceof ApiClientError && err.status === 404)) {
        reportError(err, { step: 'deregister' });
        setIsRegistered(false);
        return;
      }
    }
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(SYNCED_TOKEN_KEY);
    localStorage.removeItem(SYNCED_AT_KEY);
    localStorage.removeItem(PENDING_SYNC_KEY);
    setIsRegistered(false);
  }, []);

  // Reconciliation, not a click event: on every foreground/load, re-check the
  // FCM token against what the server last saw and re-sync if it rotated, has
  // gone stale, or a previous sync attempt left a pending marker. Runs only
  // when permission was already granted — this never prompts the user.
  const reconcileToken = useCallback(async () => {
    if (!messaging || Notification.permission !== 'granted') return;

    try {
      const swRegistration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL}firebase-messaging-sw.js`,
        { updateViaCache: 'none' }
      );
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });

      const syncedToken = localStorage.getItem(SYNCED_TOKEN_KEY);
      const syncedAt = Number(localStorage.getItem(SYNCED_AT_KEY) ?? 0);
      const isPending = localStorage.getItem(PENDING_SYNC_KEY) === '1';
      const isStale = Date.now() - syncedAt > RECONCILE_STALE_MS;

      if (token === syncedToken && !isPending && !isStale) return;

      const { device } = await registerDevice({
        platform: 'web',
        fcmToken: token,
        appType: 'landlord',
        deviceName: navigator.userAgent.slice(0, 100),
        previousDeviceId: localStorage.getItem(DEVICE_ID_KEY) ?? undefined,
      });
      localStorage.setItem(DEVICE_ID_KEY, device.id);
      localStorage.setItem(SYNCED_TOKEN_KEY, token);
      localStorage.setItem(SYNCED_AT_KEY, String(Date.now()));
      localStorage.removeItem(PENDING_SYNC_KEY);
      setIsRegistered(true);
    } catch (err) {
      // Silent, background reconciliation — never surfaced to the user. Mark
      // pending so the next foreground/online event retries automatically.
      localStorage.setItem(PENDING_SYNC_KEY, '1');
      reportError(err, { step: 'reconcile-token' });
    }
  }, []);

  useEffect(() => {
    reconcileToken();

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        reconcileToken();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', reconcileToken);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', reconcileToken);
    };
  }, [reconcileToken]);

  // Foreground message handler — app is open and focused. FCM delivers here
  // instead of invoking the service worker's onBackgroundMessage, so this is
  // the only place a foreground push can be surfaced; mirrors
  // firebase-messaging-sw.template.js's showNotification call (same
  // title/body/icon), with the click resolving to the BR-75 `path` the
  // service layer attaches.
  useEffect(() => {
    if (!messaging || !handleForegroundMessages) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      if (Notification.permission !== 'granted') return;

      const title = payload.data?.title ?? 'New Notification';
      const body = payload.data?.body ?? '';
      const path = payload.data?.path;

      const notification = new Notification(title, {
        body,
        icon: `${import.meta.env.BASE_URL}icons/icon-192.png`,
      });
      notification.onclick = () => {
        window.focus();
        if (path) navigate(path);
        notification.close();
      };
    });
    return unsubscribe;
  }, [handleForegroundMessages, navigate]);

  return { permissionState, isRegistered, requestPermissionAndRegister, deregister };
}
